const post = {
  "slug": "connect-razorpay-to-checkout-dm",
  "title": "How to Connect Razorpay to Checkout DM: Keys and Webhook",
  "seoTitle": "Connect Razorpay to Checkout DM",
  "description": "Where to find your Razorpay Key ID, Key Secret and Webhook Secret, which webhook events to turn on and how to avoid common mix-ups between Test and Live mode.",
  "excerpt": "Connecting Razorpay needs three values: a Key ID, a Key Secret and a Webhook Secret. Here is where each one lives and which events to subscribe to.",
  "category": "Payments",
  "tags": [
    "Razorpay Instagram",
    "Razorpay API keys",
    "Razorpay webhook",
    "Instagram payments India"
  ],
  "date": "2026-11-20",
  "cover": {
    "src": "/blog-images/connect-razorpay-to-checkout-dm-cover.png",
    "alt": "Razorpay Key ID, Key Secret and Webhook Secret fields ready to paste into Checkout DM"
  },
  "blocks": [
    {
      "type": "p",
      "text": "To take payments in your Instagram DMs through Razorpay, Checkout DM needs three things from your Razorpay Dashboard: a Key ID, a Key Secret and a Webhook Secret. The first two let Checkout DM create payment links for you. The third lets it confirm that a payment notification really came from Razorpay. This guide shows where each one lives."
    },
    {
      "type": "callout",
      "tone": "peach",
      "title": "Treat the secrets like passwords",
      "text": "Your Key Secret and Webhook Secret are sensitive. Razorpay only shows the Key Secret once, at the moment it is generated, so copy it somewhere safe, or straight into your Checkout DM payment settings, right away."
    },
    {
      "type": "h2",
      "text": "What each value does"
    },
    {
      "type": "table",
      "headers": [
        "Value",
        "Where it comes from",
        "What it does"
      ],
      "rows": [
        [
          "Key ID and Key Secret",
          "Razorpay Dashboard, Account and Settings, API keys",
          "Authenticate every payment request Checkout DM sends on your behalf"
        ],
        [
          "Webhook Secret",
          "A secret you type yourself when you add the webhook in Razorpay",
          "Lets Checkout DM verify that a payment notification really came from Razorpay"
        ]
      ]
    },
    {
      "type": "h2",
      "text": "Step 1: Key ID and Key Secret"
    },
    {
      "type": "ol",
      "items": [
        "**Log in** to the Razorpay Dashboard with your business account.",
        "**Choose Test Mode or Live Mode** with the toggle near the top. Test keys are for trying things out with fake payments, and Live keys are for real customers. Each mode has its own separate Key ID, Key Secret and Webhook Secret, so do not mix them up.",
        "**Open Account and Settings, then API keys,** under the Website and app settings card.",
        "**Generate your keys.** If you have not generated keys before, click Generate Key. Razorpay shows the Key ID and Key Secret together, with a Download button. Save both now."
      ]
    },
    {
      "type": "p",
      "text": "If you have lost the Secret, you will need to click Regenerate Key. That creates a brand-new pair, and the old Key ID and Secret stop working immediately, so update them everywhere they were in use, including Checkout DM."
    },
    {
      "type": "h2",
      "text": "Step 2: the webhook and its secret"
    },
    {
      "type": "p",
      "text": "A webhook is how Razorpay tells Checkout DM, in real time, that a payment succeeded or failed. Setting one up takes four steps."
    },
    {
      "type": "ol",
      "items": [
        "**Open Settings, then Webhooks** in Razorpay and click Add New Webhook.",
        "**Paste the webhook URL.** The URL is the same for every Checkout DM merchant and is shown in Checkout DM\u2019s Razorpay keys guide, so copy it from there.",
        "**Type your own secret.** Razorpay does not generate this one for you. Use a long, random string. Whatever you type is the exact value you will paste into Checkout DM\u2019s Razorpay Webhook Secret field, and the two must match exactly or Checkout DM cannot verify incoming webhooks.",
        "**Choose events and save.** Tick the events described below, click Save Webhook, and reopen it from your Webhooks list any time to check the URL, the enabled events or the secret."
      ]
    },
    {
      "type": "h2",
      "text": "Which events to subscribe to"
    },
    {
      "type": "ul",
      "items": [
        "**payment.captured:** the payment went through and the money is captured. This is the main event Checkout DM uses to mark an order as paid.",
        "**payment.failed:** the payment attempt failed. Checkout DM uses it to let the customer know and, where possible, prompt a retry.",
        "**payment.authorized:** the bank authorised the payment but it is not yet captured. Useful for catching payments confirmed a little later than usual.",
        "**order.paid:** fires once an order is fully paid, tying the payment back to the order it belongs to."
      ]
    },
    {
      "type": "p",
      "text": "At minimum, Checkout DM needs payment.captured and payment.failed. It is also worth ticking payment.authorized and order.paid if you see them, because they cover edge cases such as delayed bank confirmations."
    },
    {
      "type": "h2",
      "text": "Step 3: paste everything into Checkout DM"
    },
    {
      "type": "p",
      "text": "Paste your Key ID, Key Secret and Webhook Secret into the Razorpay section of your Payment Settings in Checkout DM. Nothing is charged until it is saved and verified. Start in Test Mode with a test order to be sure the pieces work, then repeat with your Live keys and Live webhook when you are ready for real customers."
    },
    {
      "type": "h2",
      "text": "If something goes wrong"
    },
    {
      "type": "p",
      "text": "If Checkout DM cannot create a payment link for an order, the order shows the status link failed in your Orders tab and the customer cannot pay until it is fixed. Check that the Key ID and Key Secret were copied exactly and come from the same mode, Test or Live, as the webhook you set up. Our [status badges guide](/blog/checkout-dm-status-badges-explained) lists every order status."
    },
    {
      "type": "p",
      "text": "Razorpay is one way to get paid in DMs. If you are choosing between a payment gateway and a UPI QR code or UPI ID, our guide to [accepting payments on Instagram: UPI versus a gateway](/blog/accept-payments-instagram-upi-vs-gateway) compares them, and [how to take payments on Instagram in India](/blog/take-payments-on-instagram-india) covers the wider picture."
    }
  ],
  "related": [
    {
      "to": "/razorpay-keys-guide",
      "title": "Razorpay Keys Guide",
      "description": "Step-by-step help finding your keys and webhook secret."
    },
    {
      "to": "/stripe-keys-guide",
      "title": "Stripe Keys Guide",
      "description": "Find your Stripe keys if you also use Stripe."
    },
    {
      "to": "/status-guide",
      "title": "Status Badges Guide",
      "description": "What each order status means, including link failed."
    }
  ],
  "faqs": [
    {
      "q": "What do I need from Razorpay to connect it?",
      "a": "A Key ID, a Key Secret and a Webhook Secret."
    },
    {
      "q": "Where do I find the Key Secret again if I lost it?",
      "a": "Razorpay only shows it once. You need to click Regenerate Key, which creates a new pair and stops the old one from working immediately."
    },
    {
      "q": "Where does the Webhook Secret come from?",
      "a": "You type it yourself when you create the webhook in Razorpay. Use a long random string, and paste the exact same value into Checkout DM."
    },
    {
      "q": "Which webhook events does Checkout DM need?",
      "a": "At minimum payment.captured and payment.failed. Ticking payment.authorized and order.paid as well is recommended to cover delayed confirmations."
    },
    {
      "q": "Where do I paste the three values?",
      "a": "Into the Razorpay section of your Payment Settings in Checkout DM. Nothing is charged until it is saved and verified."
    },
    {
      "q": "Can I use Test and Live keys together?",
      "a": "Each mode has its own Key ID, Key Secret and Webhook Secret, so keep them paired. Test with Test Mode first, then switch everything to Live."
    }
  ]
};

export default post;
